import React from "react";
import { Loader2, Wrench, CheckCircle2 } from "lucide-react";
import { AgentSystemEvent } from "../../../redux/reducers/Execution";
import { formatToolDisplayName } from "../../utils/thinkingTab";

/** Valid event types streamed from the agent's system events. */
export enum ThinkingEventType {
  TOOL_START = "tool_start",
  TOOL_COMPLETE = "tool_complete",
  TOOL_INPUT = "tool_input",
  AGENT_THOUGHT = "agent_thought",
}

interface ThinkingEventItemProps {
  event: AgentSystemEvent;
  isActive: boolean;
  isCompleted?: boolean;
  /** When >1, consecutive same-tool calls are collapsed into one item with a count badge. */
  repeatCount?: number;
}

/** Renders a single system event in the Thinking tab timeline. */
const ThinkingEventItem: React.FC<ThinkingEventItemProps> = ({
  event,
  isActive,
  isCompleted = false,
  repeatCount,
}) => {
  const displayName = formatToolDisplayName(event.toolName);

  switch (event.eventType) {
    case ThinkingEventType.TOOL_START:
      return (
        <div className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-0.5">
            {isActive ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            ) : isCompleted ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : (
              <Wrench className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {displayName}
              {repeatCount && repeatCount > 1 && (
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  ×{repeatCount}
                </span>
              )}
            </p>
            {event.details && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {event.details}
              </p>
            )}
          </div>
          <span className="text-xs text-muted-foreground/60 flex-shrink-0">
            {new Date(event.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        </div>
      );

    case ThinkingEventType.TOOL_COMPLETE:
      return (
        <div className="flex items-center gap-3 py-1 pl-7">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-xs text-muted-foreground">
            Complete
            {event.details && event.details !== "success"
              ? ` (${event.details})`
              : ""}
          </span>
        </div>
      );

    case ThinkingEventType.AGENT_THOUGHT:
      return (
        <div className="py-2 pl-1">
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            {event.details}
          </p>
        </div>
      );

    case ThinkingEventType.TOOL_INPUT:
      return (
        <div className="py-0.5 pl-7">
          <p className="text-xs text-muted-foreground truncate">
            {event.details}
          </p>
        </div>
      );

    default:
      // Generic fallback for unknown event types
      return (
        <div className="flex items-center gap-3 py-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
          <span className="text-xs text-muted-foreground">
            {event.eventType}
            {event.toolName ? `: ${event.toolName}` : ""}
          </span>
        </div>
      );
  }
};

export default ThinkingEventItem;
